import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../shared/contexts/AuthContext";
import { loadItem, removeItem, saveItem, storageKeys } from "../../shared/storage/index";
import { registerUsername } from "../../shared/utils/usernameValidation";
import {
  DEFAULT_ONBOARDING_DRAFT,
  draftToProfile,
  getNextOnboardingPath,
  getOnboardingStepByKey,
  getPrevOnboardingPath,
  hydrateDraftWithPhotos,
  mergeDraftSources,
  normalizeOnboardingDraft,
  ONBOARDING_MODE,
  ONBOARDING_STEP_COUNT,
  profileToDraft,
  readLocalOnboardingDraft,
  resolveOnboardingMode,
  slimDraftForStorage,
} from "./onboardingDraft";
import { clearOnboardingResumePath, saveOnboardingResumePath } from "./onboardingSession";
import { loadOwnProfile, notifyProfileUpdated, saveOwnProfile, saveOwnPhotos, fetchOwnProfile } from "../profile/profileModel";
import { useOnboardingSheet } from "./OnboardingSheet";

const OnboardingContext = createContext(null);

function buildInitialDraft(mode, userId) {
  if (mode === ONBOARDING_MODE.EDIT) {
    const apiDraft = profileToDraft(loadOwnProfile(userId));
    if (loadItem(storageKeys.onboardingComplete)) {
      return apiDraft;
    }
    return mergeDraftSources(apiDraft, readLocalOnboardingDraft());
  }

  const storedDraft = loadItem(storageKeys.onboardingDraft, DEFAULT_ONBOARDING_DRAFT);
  const storedPhotos = loadItem(storageKeys.profilePhotos, []);
  const hydrated = hydrateDraftWithPhotos(storedDraft, storedPhotos);

  const hadInlineDraftPhotos = Array.isArray(storedDraft?.photos) && storedDraft.photos.some(Boolean);
  const hadInlineSlots = Array.isArray(storedDraft?.photoSlots) && storedDraft.photoSlots.some(
    (slot) => typeof slot === "string" && slot.trim() && slot !== "__saved__",
  );
  if (hadInlineDraftPhotos || hadInlineSlots) {
    persistCreateDraftToStorage(hydrated);
  }

  return hydrated;
}

function persistCreateDraftToStorage(draft) {
  const normalized = normalizeOnboardingDraft(draft);
  const photos = normalized.photos;

  if (photos.length) {
    if (!saveItem(storageKeys.profilePhotos, photos)) {
      return {
        ok: false,
        error: "Could not save your photos. Try fewer or smaller images.",
      };
    }
  } else {
    removeItem(storageKeys.profilePhotos);
  }

  if (!saveItem(storageKeys.onboardingDraft, slimDraftForStorage(normalized))) {
    return {
      ok: false,
      error: "Could not save your progress. Your browser storage may be full.",
    };
  }

  return { ok: true };
}

export function OnboardingProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUser } = useAuth();
  const userId = user?.id ?? "me";
  const { requestClose } = useOnboardingSheet();
  const mode = resolveOnboardingMode(location.state);
  const isEditMode = mode === ONBOARDING_MODE.EDIT;

  const [draft, setDraftState] = useState(() => buildInitialDraft(mode, userId));
  const [isDraftLoading, setIsDraftLoading] = useState(Boolean(user?.id));
  const [progressSaveError, setProgressSaveError] = useState(null);
  const [saveToastVisible, setSaveToastVisible] = useState(false);
  const saveToastTimerRef = useRef(null);
  const draftRef = useRef(draft);
  draftRef.current = draft;

  useEffect(() => () => {
    if (saveToastTimerRef.current) {
      window.clearTimeout(saveToastTimerRef.current);
    }
  }, []);

  useEffect(() => {
    if (!user?.id) {
      setIsDraftLoading(false);
      return undefined;
    }

    if (isEditMode) {
      let cancelled = false;

      async function hydrateEditDraft() {
        setIsDraftLoading(true);
        try {
          const profile = await fetchOwnProfile(userId);
          if (cancelled) return;

          const apiDraft = profileToDraft(profile);
          const merged = loadItem(storageKeys.onboardingComplete)
            ? apiDraft
            : mergeDraftSources(apiDraft, readLocalOnboardingDraft());
          setDraftState(merged);
        } finally {
          if (!cancelled) {
            setIsDraftLoading(false);
          }
        }
      }

      hydrateEditDraft();
      return () => {
        cancelled = true;
      };
    }

    let cancelled = false;

    async function hydrateCreateDraft() {
      setIsDraftLoading(true);
      try {
        const profile = await fetchOwnProfile(userId);
        if (cancelled) return;

        if (profile?.username || profile?.photos?.length || profile?.city || profile?.age) {
          setDraftState(mergeDraftSources(profileToDraft(profile), readLocalOnboardingDraft()));
        }
      } finally {
        if (!cancelled) {
          setIsDraftLoading(false);
        }
      }
    }

    hydrateCreateDraft();
    return () => {
      cancelled = true;
    };
  }, [isEditMode, userId, user?.id]);

  useEffect(() => {
    if (isEditMode) return;
    if (location.pathname.endsWith("/success")) return;
    if (loadItem(storageKeys.onboardingComplete)) return;
    saveOnboardingResumePath(location.pathname);
  }, [location.pathname, isEditMode]);

  const persistDraft = useCallback((nextDraft) => {
    const normalized = normalizeOnboardingDraft(nextDraft);
    setDraftState(normalized);
    if (!isEditMode) {
      persistCreateDraftToStorage(normalized);
    }
    return normalized;
  }, [isEditMode]);

  const updateDraft = useCallback((patch) => {
    setDraftState((prev) => {
      const merged = normalizeOnboardingDraft({
        ...prev,
        ...patch,
        contact: { ...prev.contact, ...(patch.contact || {}) },
      });
      if (!isEditMode) {
        persistCreateDraftToStorage(merged);
      }
      return merged;
    });
  }, [isEditMode]);

  const saveProgressToServer = useCallback(async (stepKey) => {
    if (isEditMode || !user?.id) {
      return { ok: true };
    }

    const latestDraft = normalizeOnboardingDraft(draftRef.current);

    if (stepKey === "photos") {
      if (!latestDraft.photos.length) {
        return { ok: true };
      }

      const result = await saveOwnPhotos(latestDraft.photos, userId);
      if (!result.ok) {
        setProgressSaveError(result.error || "Could not save your photos.");
        return result;
      }

      setProgressSaveError(null);
      return result;
    }

    const profile = draftToProfile(latestDraft, userId);
    const result = await saveOwnProfile(profile, { onboardingComplete: false });

    if (!result.ok) {
      setProgressSaveError(result.error || "Could not save your progress.");
      return result;
    }

    setProgressSaveError(null);
    return result;
  }, [isEditMode, user?.id, userId]);

  const goToNextStep = useCallback(async (stepKey) => {
    const nextPath = getNextOnboardingPath(stepKey);
    if (!nextPath) return;

    if (!isEditMode) {
      const saveResult = await saveProgressToServer(stepKey);
      if (!saveResult.ok) return;
      saveOnboardingResumePath(nextPath);
    }

    navigate(nextPath, { state: location.state });
  }, [navigate, isEditMode, location.state, saveProgressToServer]);

  const goToPrevStep = useCallback((stepKey) => {
    const prevPath = getPrevOnboardingPath(stepKey);
    if (prevPath) {
      if (!isEditMode) saveOnboardingResumePath(prevPath);
      navigate(prevPath, { state: location.state });
      return;
    }
    requestClose();
  }, [navigate, requestClose, isEditMode, location.state]);

  const completeOnboarding = useCallback(async (overrideUserId) => {
    const latestDraft = normalizeOnboardingDraft(draftRef.current);
    const profile = draftToProfile(latestDraft, overrideUserId ?? userId);

    if (mode === ONBOARDING_MODE.CREATE) {
      saveItem(storageKeys.onboardingDraft, slimDraftForStorage(latestDraft));
    }

    const saveResult = await saveOwnProfile(profile, {
      onboardingComplete: mode === ONBOARDING_MODE.CREATE,
    });
    if (!saveResult.ok) {
      return saveResult;
    }

    if (!saveItem(storageKeys.onboardingDraft, slimDraftForStorage(latestDraft))) {
      return {
        ok: false,
        error: "Could not save your profile progress.",
      };
    }

    if (mode === ONBOARDING_MODE.CREATE) {
      if (!saveItem(storageKeys.onboardingComplete, true)) {
        return {
          ok: false,
          error: "Could not complete onboarding.",
        };
      }
      removeItem(storageKeys.onboardingDraft);
      saveItem(storageKeys.roleSelectionSeen, true);
      clearOnboardingResumePath();
      await refreshUser();
    }

    if (latestDraft.username) {
      registerUsername(latestDraft.username, overrideUserId ?? userId);
    }

    notifyProfileUpdated();
    return { ok: true, profile: saveResult.profile };
  }, [mode, userId, refreshUser]);

  const showSaveSuccessAndExit = useCallback((onDone) => {
    setSaveToastVisible(true);

    if (saveToastTimerRef.current) {
      window.clearTimeout(saveToastTimerRef.current);
    }

    saveToastTimerRef.current = window.setTimeout(() => {
      saveToastTimerRef.current = null;
      setSaveToastVisible(false);
      onDone?.();
    }, 1000);
  }, []);

  const value = useMemo(
    () => ({
      draft,
      isDraftLoading,
      progressSaveError,
      mode,
      isEditMode,
      updateDraft,
      persistDraft,
      goToNextStep,
      goToPrevStep,
      completeOnboarding,
      showSaveSuccessAndExit,
      saveToastVisible,
      stepCount: ONBOARDING_STEP_COUNT,
      getStepMeta: getOnboardingStepByKey,
    }),
    [draft, isDraftLoading, progressSaveError, mode, isEditMode, updateDraft, persistDraft, goToNextStep, goToPrevStep, completeOnboarding, showSaveSuccessAndExit, saveToastVisible],
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error("useOnboarding must be used within OnboardingProvider");
  }
  return context;
}
